import { useRef, useEffect } from 'react';

interface UseAutoFocusOptions {
  condition?: boolean;
  delay?: boolean;
}

/**
 * Custom hook for auto-focusing elements with requestAnimationFrame
 * 
 * @param options Configuration options
 * @param options.condition Whether to focus the element (default: true)
 * @param options.delay Whether to use requestAnimationFrame for timing (default: false)
 * @returns Ref to attach to the target element
 * 
 * @example
 * // Basic usage
 * const inputRef = useAutoFocus<HTMLTextAreaElement>({ condition: !hasConversation });
 * return <Textarea ref={inputRef} />;
 * 
 * @example  
 * // With delay for animations
 * const inputRef = useAutoFocus<HTMLInputElement>({ 
 *   condition: isModalOpen, 
 *   delay: true 
 * });
 */
export const useAutoFocus = <T extends HTMLElement = HTMLTextAreaElement>(
  options: UseAutoFocusOptions = {}
) => {
  const { condition = true, delay = false } = options;
  const ref = useRef<T>(null);
  
  useEffect(() => {
    if (condition && ref.current) {
      if (delay) {
        // Use requestAnimationFrame for better performance than setTimeout
        const rafId = requestAnimationFrame(() => {
          ref.current?.focus();
        });
        
        // Cleanup animation frame to prevent memory leaks
        return () => cancelAnimationFrame(rafId);
      } else {
        // Focus immediately
        ref.current.focus();
      }
    }
  }, [condition, delay]);
  
  return ref;
};